import * as Sentry from "@sentry/react";
import type { NaniexpertResponse } from "@/types/apis/naniexpert";
import { getToken } from "@/utils/auth/helpers";
import { handleUnauthorized } from "@/utils/auth/handleUnauthorized";
import { UnauthorizedError } from "@/utils/auth/UnauthorizedError";
import { ApiError, EXPECTED_BUSINESS_CODES } from "@/apis/naniexpert/ApiError";
import { sentryMarkAsReported } from "@/utils/sentry.helper";

const SUCCESS_CODE = 200;

type FetchNaniexpertOptions = {
  url: string;
  body?: unknown;
  method?: string;
};

function getGeneralizedPath(url: string): string {
  return url.replace(/\/\d+/g, "/{id}");
}

export const fetchNaniexpert = async <T>(
  options: FetchNaniexpertOptions
): Promise<T> => {
  const { url, body, method = "POST" } = options;
  const fullUrl = `${import.meta.env.VITE_API_URL}/api${url}`;
  const cleanPath = getGeneralizedPath(url);

  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["authorization"] = token;
  }

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTag("type", "network_crash");
      scope.setTag("api_source", "naniexpert");
      scope.setFingerprint(["network-crash", method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setExtra("method", method);
      Sentry.captureException(e);
    });
    sentryMarkAsReported(e);
    throw e;
  }

  if (response.status === 401) {
    handleUnauthorized();
    throw new UnauthorizedError();
  }

  const result = (await response
    .json()
    .catch(() => null)) as NaniexpertResponse<unknown> | null;

  if (response.status === SUCCESS_CODE && result?.error === SUCCESS_CODE) {
    return result.data as T;
  }

  // NOTE: 後端現階段 API 均回傳 http status 200，實際錯誤判斷基準為 body.error code
  // code 優先取 body.error（業務碼），無法解析 body 時退回 HTTP status。
  const code = result?.error ?? response.status;
  const message = result?.message ?? "ERROR";
  const apiError = new ApiError(response.status, code, message);

  if (!EXPECTED_BUSINESS_CODES.has(code)) {
    Sentry.withScope((scope) => {
      scope.setTag("api_path", cleanPath);
      scope.setTag("api_method", method);
      scope.setTag("api_source", "naniexpert");
      scope.setTag("http_status", response.status);
      scope.setTag("api_code", String(code));
      scope.setFingerprint([method, cleanPath, String(code)]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setLevel("warning");
      Sentry.captureMessage(
        `API Error: ${method} ${cleanPath} [http:${response.status} code:${code}]`
      );
    });
  }

  sentryMarkAsReported(apiError);
  throw apiError;
};
